import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { CreateDiscountDTO, DiscountResponseDTO, UpdateDiscountDTO } from '@libs/dtos/discount/discount.dto';
import { DiscountService } from '@services/discount/discount.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ValidationService } from '@services/ui/validation.service';
import { UserService } from '@services/user/user.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { DatepickerComponent } from '@shared/components/datepicker/datepicker.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, of, Subject, switchMap } from 'rxjs';

/** Form-level rule: the end date is after the start date (error shown on the end date) */
function endAfterStart(group: AbstractControl): ValidationErrors | null {
	const start = group.get('startDate')?.value;
	const end = group.get('endDate')?.value;
	const invalid = start && end && new Date(end) < new Date(start);
	const endControl = group.get('endDate');
	if (endControl) {
		const { dateOrder: _, ...others } = endControl.errors ?? {};
		endControl.setErrors(invalid ? { ...others, dateOrder: true } : Object.keys(others).length ? others : null);
	}
	return null;
}

function endOfDay(date: Date): Date {
	const end = new Date(date);
	end.setHours(23, 59, 59, 999);
	return end;
}

/** Users shown per search in the discount's user select (the API caps lists at 100) */
const UserSearchLimit = 50;

@Component({
	selector: 'app-discount-crud',
	imports: [SharedModule, ButtonComponent, InputComponent, SelectComponent, DatepickerComponent],
	templateUrl: './discount-crud.component.html',
	styleUrl: './discount-crud.component.scss',
})
export class DiscountCrudComponent implements OnInit {
	@Output() discountUpdated = new EventEmitter<void>();

	visible = false;
	isEditing = false;
	currentDiscountId: string | null = null;
	dialogTitle = 'Nuevo Descuento';
	actionButtonLabel = 'Crear Descuento';
	isLoading = false;
	discountForm: FormGroup;

	typeOptions: any[] = [];

	userOptions: { label: string; value: number }[] = [];

	constructor(
		private fb: FormBuilder,
		private discountService: DiscountService,
		private userService: UserService,
		private validationService: ValidationService,
		private messageAlertService: MessageAlertService,
		private translocoService: TranslocoService,
	) {
		this.typeOptions = [
			{ label: this.translocoService.translate('discountsCrud.typeOptions.percentage'), value: 'PERCENTAGE' },
			{ label: this.translocoService.translate('discountsCrud.typeOptions.fixed'), value: 'FIXED_AMOUNT' },
		];

		// Same limits as the backend schema: code 50 chars, uses a whole number >= 1, end after start
		this.discountForm = this.fb.group(
			{
				name: ['', [Validators.required, Validators.maxLength(100)]],
				type: ['PERCENTAGE', [Validators.required]],
				value: [null, [Validators.required, Validators.min(0.01), Validators.max(100)]],
				userId: [null],
				code: [null, [Validators.maxLength(50)]],
				startDate: [null],
				endDate: [null],
				maxUses: [null, [Validators.min(1), Validators.pattern(/^\d+$/)]],
				minOrderAmount: [null, [Validators.min(0.01)]],
			},
			{ validators: [endAfterStart] },
		);

		// A percentage is at most 100; a fixed amount has no upper limit
		this.discountForm.get('type')?.valueChanges.subscribe((type) => {
			const value = this.discountForm.get('value');
			value?.setValidators(
				type === 'PERCENTAGE'
					? [Validators.required, Validators.min(0.01), Validators.max(100)]
					: [Validators.required, Validators.min(0.01)],
			);
			value?.updateValueAndValidity({ emitEvent: false });
		});

		// A discount is for one user OR for one code (neither = everyone): filling one disables the other,
		// instead of silently dropping the code when both were filled
		this.discountForm.get('userId')?.valueChanges.subscribe((userId) => this.toggle('code', !userId));
		this.discountForm.get('code')?.valueChanges.subscribe((code) => this.toggle('userId', !`${code ?? ''}`.trim()));
	}

	private toggle(field: 'code' | 'userId', enabled: boolean): void {
		const control = this.discountForm.get(field);
		if (!control || control.enabled === enabled) return;
		if (enabled) control.enable({ emitEvent: false });
		else control.disable({ emitEvent: false });
	}

	/** The users for the select could not be loaded (the select shows why it is empty) */
	usersLoadError = false;
	usersLoading = false;

	/** Option of the user of the discount being edited, when it is not in the loaded list */
	private savedUserOption: { label: string; value: number } | null = null;

	/** Users matching what is typed in the select's search box (server side: there can be thousands) */
	private readonly userSearch$ = new Subject<string>();
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		// Users are loaded when the form opens (show), not every time the Discounts tab is visited
		this.userSearch$
			.pipe(
				debounceTime(300),
				map((term) => term.trim()),
				distinctUntilChanged(),
				switchMap((term) => this.fetchUsers(term)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}

	onUserSearch(term: string): void {
		this.userSearch$.next(term);
	}

	/** First page of users (no search) when the form opens; retried on the next open if it failed */
	loadUsers(): void {
		if (this.userOptions.length && !this.usersLoadError) return;
		this.fetchUsers('').subscribe();
	}

	private fetchUsers(search: string) {
		this.usersLoadError = false;
		this.usersLoading = true;
		return this.userService.getAllUsers({ limit: UserSearchLimit, ...(search && { search }) }).pipe(
			map((res: any) => {
				const loaded: { label: string; value: number }[] = (res.data || []).map((u: any) => ({
					label: `${[u.name, u.lastName].filter(Boolean).join(' ') || u.email} (${u.email})`,
					value: u.id,
				}));
				// The selected user stays selectable (and labelled) whatever the search returns
				const selectedId = this.discountForm.get('userId')?.value;
				const keep = [this.savedUserOption, this.userOptions.find((o) => o.value === selectedId)].filter(
					(o): o is { label: string; value: number } => !!o && !loaded.some((l) => l.value === o.value),
				);
				this.userOptions = [
					...keep.filter((o, i) => keep.findIndex((k) => k.value === o.value) === i),
					...loaded,
				];
			}),
			catchError(() => {
				// The select keeps the options it had; the next show() retries
				this.usersLoadError = true;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('discountsCrud.messages.usersLoadError'),
				);
				return of(null);
			}),
			finalize(() => {
				this.usersLoading = false;
			}),
		);
	}

	show(discount?: DiscountResponseDTO): void {
		this.loadUsers();
		// The user of the discount may be outside the loaded page of users: keep it selectable by name
		if (discount?.userId && !this.userOptions.some((o) => o.value === discount.userId)) {
			this.savedUserOption = { label: discount.userName || `#${discount.userId}`, value: discount.userId };
			this.userOptions = [this.savedUserOption, ...this.userOptions];
		}
		this.visible = true;
		this.isEditing = !!discount;
		this.currentDiscountId = discount?.id?.toString() || null;
		this.dialogTitle = this.translocoService.translate(
			this.isEditing ? 'discountsCrud.dialogTitleFull.edit' : 'discountsCrud.dialogTitleFull.new',
		);
		this.actionButtonLabel = this.translocoService.translate(
			this.isEditing ? 'discountsCrud.actionButton.edit' : 'discountsCrud.actionButton.new',
		);

		if (this.isEditing && discount) {
			this.discountForm.patchValue({
				name: discount.name,
				type: discount.type,
				value: discount.value,
				userId: discount.userId || null,
				code: discount.code || null,
				startDate: discount.startDate ? new Date(discount.startDate) : null,
				endDate: discount.endDate ? new Date(discount.endDate) : null,
				maxUses: discount.maxUses,
				minOrderAmount: discount.minOrderAmount,
			});
		} else {
			this.discountForm.reset({
				type: 'PERCENTAGE',
			});
		}
		// Re-evaluate which of user/code is editable for the values just loaded
		this.toggle('code', !this.discountForm.get('userId')?.value);
		this.toggle('userId', !`${this.discountForm.get('code')?.value ?? ''}`.trim());
	}

	onSubmit(): void {
		if (this.isLoading) return;

		if (this.discountForm.invalid) {
			this.discountForm.markAllAsTouched();
			return;
		}

		this.isLoading = true;
		const formData = this.discountForm.getRawValue();

		const scope = formData.userId ? 'USER' : formData.code ? 'CODE' : 'GLOBAL';

		const payload: any = {
			...formData,
			scope,
			// new discounts start active; editing must not reactivate one that was switched off
			...(this.isEditing ? {} : { isActive: true }),
			userId: scope === 'USER' ? formData.userId : null,
			code: scope === 'CODE' ? formData.code : null,
			startDate:
				formData.startDate instanceof Date ? formData.startDate.toISOString() : formData.startDate || null,
			// The end date is a calendar day: it lasts until the end of that day, not until its first minute
			endDate:
				formData.endDate instanceof Date ? endOfDay(formData.endDate).toISOString() : formData.endDate || null,
		};

		// Clear empty optional fields
		if (!payload.maxUses) payload.maxUses = null;
		if (!payload.minOrderAmount) payload.minOrderAmount = null;

		const request$ =
			this.isEditing && this.currentDiscountId
				? this.discountService.updateDiscount(this.currentDiscountId, payload as UpdateDiscountDTO)
				: this.discountService.createDiscount(payload as CreateDiscountDTO);

		// Keep the dialog open until the server answers: on error the typed values stay
		request$
			.pipe(
				catchError((err) => {
					// errors.<code> from the backend; generic key as fallback
					const errorMessage = translateApiError(
						err,
						this.translocoService,
						this.isEditing ? 'discountsCrud.messages.updateError' : 'discountsCrud.messages.createError',
					);

					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						errorMessage,
					);
					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((response) => {
				if (response) {
					this.visible = false;
					this.discountUpdated.emit(); // refresh the list only when something was saved
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate(
							this.isEditing
								? 'discountsCrud.messages.updateSuccess'
								: 'discountsCrud.messages.createSuccess',
						),
					);
				}
			});
	}

	getErrorMessage(field: string): string | null {
		const control = this.discountForm?.get(field);
		if (control?.errors) {
			const errorKey = Object.keys(control.errors)[0];
			return this.validationService.getValidatorErrorMessage(errorKey, control.errors[errorKey]) || null;
		}
		return null;
	}

	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}
}
